import { useState } from "react";
import { Plus } from "lucide-react";
import { useChannelStore } from "../../stores/channelStore";
import { useModalStore } from "../../stores/modalStore";
import { useMemberStore } from "../../stores/memberStore";
import { useServerStore } from "../../stores/serverStore";
import { useVoiceStore } from "../../stores/voiceStore";
import { useAeThex } from "../../components/AeThexProvider";
import { ServerHeader } from "./ServerHeader";

export default function ChannelSidebar({ onOpenSettings, mobileOpen, onMobileClose }) {
  const channels = useChannelStore((state) => state.channels);
  const currentChannelId = useChannelStore((state) => state.currentChannelId);
  const setCurrentChannel = useChannelStore((state) => state.setCurrentChannel);
  const isLoading = useChannelStore((state) => state.isLoading);
  const unreadCounts = useChannelStore((state) => state.unreadCounts);
  const mentionCounts = useChannelStore((state) => state.mentionCounts);

  const voiceChannelId = useVoiceStore(s => s.channelId);
  const voiceChannelName = useVoiceStore(s => s.channelName);
  const isMuted = useVoiceStore(s => s.isMuted);
  const toggleMute = useVoiceStore(s => s.toggleMute);
  const leaveVoice = useVoiceStore(s => s.leave);
  const joinVoice = useVoiceStore(s => s.join);
  const voiceParticipants = useVoiceStore(s => s.participants);

  const [collapsed, setCollapsed] = useState({});
  const toggleCategory = (cat) => setCollapsed(p => ({ ...p, [cat]: !p[cat] }));
  const onOpen = useModalStore((state) => state.onOpen);
  const getCurrentUser = useMemberStore((state) => state.getCurrentUser);

  const currentServer = useServerStore(
    (state) =>
      state.getCurrentServer?.() ||
      state.servers?.find((s) => s.id === state.currentServerId) ||
      null
  );

  const { user } = useAeThex();

  let currentUser = null;
  try {
    currentUser = getCurrentUser();
  } catch (_) {
    // ignore
  }

  const isAdmin =
    currentUser?.role?.toUpperCase() === 'ADMIN' ||
    currentUser?.role?.toUpperCase() === 'OWNER';

  const displayName =
    user?.display_name || user?.username || user?.name || 'User';

  // Group channels by category, falling back to type or 'General'
  const groupedChannels = channels.reduce((acc, channel) => {
    const cat = channel.category || channel.type || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(channel);
    return acc;
  }, {});

  return (
    <div className={`channel-sidebar${mobileOpen ? ' mobile-open' : ''}`}
      role="navigation" aria-label="Channels"
    >
      {mobileOpen && (
        <button className="mobile-sidebar-close" onClick={onMobileClose} aria-label="Close channels">✕</button>
      )}
      {/* Server Header */}
      <ServerHeader
        server={currentServer}
        role={currentUser?.role || "GUEST"}
      />

      {/* Channel List */}
      <div className="channel-list">
        {isLoading && channels.length === 0 && (
          <div className="flex flex-col items-center py-8">
            <div className="w-6 h-6 border-2 border-aethex-primary border-t-transparent animate-spin mb-3" />
            <p className="text-xs text-aethex-dim uppercase tracking-widest">Loading channels…</p>
          </div>
        )}
        {!isLoading && channels.length === 0 && (
          <div style={{ padding: '16px', textAlign: 'center', color: 'rgb(var(--aethex-dim))', fontSize: 12 }}>
            No channels yet.
          </div>
        )}
        {Object.entries(groupedChannels).map(([category, categoryChannels]) => (
          <div key={category}>
            <div
              className="channel-category"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleCategory(category)}
            >
              <span>{collapsed[category] ? '▸' : '▾'} {category}</span>
              {isAdmin && category !== "Voice Channels" && (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpen("createChannel", { category }); }}
                  className="add-channel-button"
                  title="Create channel"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>

            {!collapsed[category] && categoryChannels.map((channel) => {
              const isVoice   = channel.type === 'voice';
              const unread    = unreadCounts?.[channel.id]  || 0;
              const mention   = mentionCounts?.[channel.id] || 0;
              const isActive  = currentChannelId === channel.id;
              const inThisVC  = voiceChannelId === channel.id;

              const handleClick = () => {
                if (isVoice) {
                  if (inThisVC) {
                    leaveVoice();
                  } else {
                    joinVoice(channel.id, channel.name, user);
                  }
                } else {
                  setCurrentChannel(channel.id);
                }
              };

              const vcParticipants = isVoice && inThisVC ? voiceParticipants : [];

              return (
                <div key={channel.id}>
                  <div
                    onClick={handleClick}
                    className={`channel-item ${isActive && !isVoice ? 'active' : ''} ${inThisVC ? 'voice-active-item' : ''} ${unread > 0 && !isActive ? 'channel-unread' : ''}`}
                  >
                    <span className="channel-icon">
                      {channel.type === 'voice' ? '🔊' : channel.type === 'announcement' ? '📢' : channel.type === 'forum' ? '🗂️' : channel.type === 'stage' ? '🎙️' : '#'}
                    </span>
                    <span className="channel-name">{channel.name}</span>
                    {inThisVC && <span style={{ fontSize: 9, color: '#22c55e', fontWeight: 700, marginLeft: 'auto' }}>● LIVE</span>}
                    {!isActive && !inThisVC && mention > 0 && (
                      <span className="channel-badge mention">{mention > 99 ? '99+' : mention}</span>
                    )}
                    {!isActive && !inThisVC && unread > 0 && mention === 0 && (
                      <span className="channel-badge">{unread > 99 ? '99+' : unread}</span>
                    )}
                  </div>
                  {/* Voice occupant list under the channel row */}
                  {isVoice && inThisVC && vcParticipants.length > 0 && (
                    <div style={{ paddingLeft: 28 }}>
                      {vcParticipants.map((p) => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 6px 2px 0', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'linear-gradient(135deg,#22c55e,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                            {p.avatar || p.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{p.name}</span>
                          {p.isMuted && <span style={{ fontSize: 9, opacity: 0.5 }}>🔇</span>}
                          {p.isSpeaking && <span style={{ fontSize: 9, color: '#22c55e' }}>●</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Voice Connected Bar */}
      {voiceChannelId && (
        <div className="voice-connected-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
            <span style={{ color: '#22c55e', fontSize: 11, animation: 'pulse 2s infinite' }}>●</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#22c55e' }}>Voice Connected</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                🔊 {voiceChannelName || 'Voice Channel'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            <button
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
              style={{ width: 26, height: 26, borderRadius: 5, background: isMuted ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.07)', border: `1px solid ${isMuted ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`, color: isMuted ? '#ef4444' : 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >{isMuted ? '🔇' : '🎤'}</button>
            <button
              onClick={leaveVoice}
              title="Leave voice"
              style={{ width: 26, height: 26, borderRadius: 5, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >📞</button>
          </div>
        </div>
      )}

      {/* User Presence - Bottom Panel */}
      <div className="user-presence">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <div
            style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10, background: 'linear-gradient(135deg, rgb(var(--aethex-primary)/0.8), rgb(var(--aethex-primary)/0.4))', cursor: 'pointer', flexShrink: 0, color: '#fff' }}
            onClick={() => onOpen("userProfile", { member: currentUser })}
            title="View profile"
          >
            {currentUser?.avatar || displayName?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button onClick={() => onOpen("statusModal")} style={{ width: 20, height: 20, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10 }} title="Status">🟢</button>
            <button onClick={() => onOpenSettings?.()} style={{ width: 20, height: 20, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10 }} title="Settings">⚙️</button>
          </div>
        </div>

        {/* Quick Access */}
        <div style={{ display: 'flex', gap: 2, paddingTop: 4, borderTop: '1px solid rgb(var(--aethex-border))' }}>
          {[
            { icon: '👥', label: 'Friends',        action: () => onOpen('friends') },
            { icon: '🔍', label: 'Search',         action: () => onOpen('search') },
            { icon: '💬', label: 'Group DM',       action: () => onOpen('groupDM') },
            { icon: '🔔', label: 'Notifications',  action: () => onOpen('notifPrefs'), badge: true },
            { icon: '📌', label: 'Pinned',         action: () => onOpen('pinnedMessages') },
            { icon: '🤖', label: 'Bot Commands',   action: () => onOpen('botCommands') },
            { icon: '✨', label: "What's New",     action: () => onOpen('whatsNew') },
          ].map(({ icon, label, action, badge }) => (
            <button
              key={label}
              onClick={action}
              style={{ flex: 1, padding: '3px 2px', borderRadius: 4, textAlign: 'center', background: 'none', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.15s', fontSize: 14 }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--aethex-surface))'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              title={label}
            >
              {icon}
              {badge && <div style={{ position: 'absolute', top: 2, right: 4, width: 6, height: 6, background: '#ef4444', borderRadius: '50%' }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
